'use client';

import { useEffect, useRef } from 'react';

import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { installHeroSwirlPerfBenchWhenEnabled } from '@/lib/perfLab';
import { loadScrollSceneRuntime } from '@/motion/scrollScene';

import styles from './HeroScrollScene.module.css';
import { createHeroScrollScene } from './heroScrollScene.timeline';
import { HeroSwirlClipDef } from './HeroSwirlCircle';

installHeroSwirlPerfBenchWhenEnabled();

type HeroScrollSceneProps = {
  /** Hero (and any sticky-slot children) — stay Server Components; the scene only wraps them. */
  children: React.ReactNode;
};

// Start fetching the gsap chunk as soon as this module evaluates (in parallel
// with hydration) so scroll-linked animation attaches as early as possible.
// The scene layout itself is pure CSS, so a late load never shifts layout.
if (
  typeof window !== 'undefined' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
) {
  void loadScrollSceneRuntime();
}

/**
 * Client island for the hero → Introduction scroll transition. Renders the
 * scroll runway + sticky hero slot and an invisible swirl-circle transform
 * proxy — laid out by CSS from first paint — plus the SVG clipPath definitions
 * that mask `#introduction` and StickyNav. Choreography is handed to GSAP
 * (dynamically loaded, see `createHeroScrollScene`). Under reduced motion the
 * CSS media query collapses the markup to the static hero.
 */
export function HeroScrollScene({ children }: HeroScrollSceneProps) {
  const runwayRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const clipPathShapeRef = useRef<SVGPathElement>(null);
  const navClipPathShapeRef = useRef<SVGPathElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Re-runs when the reduced-motion preference flips; `gsap.matchMedia`
  // inside the scene also guards it, so this mainly avoids loading GSAP at
  // all for reduced-motion visitors.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const runway = runwayRef.current;
    const circle = circleRef.current;
    const clipPathShape = clipPathShapeRef.current;
    const navClipPathShape = navClipPathShapeRef.current;
    if (!runway || !circle || !clipPathShape || !navClipPathShape) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    void loadScrollSceneRuntime().then((runtime) => {
      if (cancelled) return;
      cleanup = createHeroScrollScene(runtime, {
        runway,
        circle,
        clipPathShape,
        navClipPathShape,
      });
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [prefersReducedMotion]);

  return (
    <div ref={runwayRef} className={styles.runway} data-hero-swirl-runway="">
      <HeroSwirlClipDef pathRef={clipPathShapeRef} navPathRef={navClipPathShapeRef} />
      <div className={styles.slot}>
        {children}
        <div className={styles.circleLayer} aria-hidden>
          {/* Invisible geometry proxy — GSAP transforms this; clip sync reads it. */}
          <div ref={circleRef} className={styles.circle} />
        </div>
      </div>
    </div>
  );
}
